import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CreditCard, AlertCircle, Printer } from 'lucide-react';
import AdminLayout from '../../../layouts/AdminLayout';
import { reportsApi } from '../../../services/api';
import { formatCurrency, formatDate, PaymentBadge } from '../../../utils/helpers';
import { useLanguage } from '../../../contexts/LanguageContext';

export default function ReceivablesPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['reports-receivables'],
    queryFn: () => reportsApi.getReceivables().then(r => r.data),
  });

  const total = orders.reduce((sum, o) => sum + (o.remaining_amount || 0), 0);

  const handleExportPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const { default: html2canvas } = await import('html2canvas');
    const el = document.getElementById('receivables-report');
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`LaporanPiutang-${new Date().toISOString().slice(0,10)}.pdf`);
  };

  return (
    <AdminLayout>
      <div className="space-y-6" id="receivables-report">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Laporan Piutang', 'Receivables Report')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('Pesanan yang belum lunas', 'Unpaid and partially paid orders')}</p>
          </div>
          <button onClick={handleExportPDF}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 bg-white text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-colors">
            <Printer size={15} />PDF
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5">
            <p className="text-xs font-semibold uppercase text-orange-600 tracking-wide">{t('Total Piutang', 'Total Receivables')}</p>
            <p className="text-3xl font-bold text-orange-700 mt-1">{formatCurrency(total)}</p>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase text-slate-400 tracking-wide">{t('Jumlah Pesanan', 'Orders Count')}</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{orders.length}</p>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase text-slate-400 tracking-wide">{t('Rata-rata Piutang', 'Avg. Receivable')}</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{orders.length > 0 ? formatCurrency(total / orders.length) : 'Rp 0'}</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-50">
            <CreditCard size={17} className="text-[#0f6c44]" />
            <h2 className="font-semibold text-slate-800">{t('Daftar Piutang', 'Receivables List')}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-50">
                  {[t('Kode', 'Code'), t('Pelanggan', 'Customer'), t('Total', 'Total'), t('Terbayar', 'Paid'), t('Sisa', 'Remaining'), t('Status', 'Status'), t('Tgl Dibuat', 'Created')].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? [1,2,3].map(i => (
                  <tr key={i}>{[1,2,3,4,5,6,7].map(j => <td key={j} className="px-5 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>
                )) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center">
                      <AlertCircle size={32} className="mx-auto text-green-400 mb-2" />
                      <p className="text-slate-500 text-sm font-medium">{t('Semua pesanan sudah lunas!', 'All orders are paid!')}</p>
                    </td>
                  </tr>
                ) : orders.map(o => (
                  <tr key={o.id} onClick={() => navigate(`/admin/orders/${o.id}`)}
                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer">
                    <td className="px-5 py-3.5"><span className="font-mono text-xs font-bold text-[#0f6c44] bg-emerald-50 px-2 py-0.5 rounded">{o.tracking_code}</span></td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm font-medium text-slate-800">{o.customer_name || '-'}</p>
                      <p className="text-xs text-slate-400">{o.customer_phone || ''}</p>
                    </td>
                    <td className="px-5 py-3.5 text-sm font-semibold text-slate-800 whitespace-nowrap">{formatCurrency(o.total_amount)}</td>
                    <td className="px-5 py-3.5 text-sm text-green-600 whitespace-nowrap">{formatCurrency(o.paid_amount)}</td>
                    <td className="px-5 py-3.5 text-sm font-bold text-orange-600 whitespace-nowrap">{formatCurrency(o.remaining_amount)}</td>
                    <td className="px-5 py-3.5"><PaymentBadge status={o.payment_status} lang={lang} /></td>
                    <td className="px-5 py-3.5 text-xs text-slate-400 whitespace-nowrap">{formatDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
              {!isLoading && orders.length > 0 && (
                <tfoot>
                  <tr className="border-t border-slate-100 bg-orange-50">
                    <td colSpan={4} className="px-5 py-3 text-sm font-bold text-slate-700">{t('Total Piutang', 'Total Receivables')}</td>
                    <td className="px-5 py-3 text-sm font-bold text-orange-600">{formatCurrency(total)}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
